import React, {useState, useRef, useEffect} from 'react';
import {Tooltip, Box, Button, Typography} from '@mui/material';
import './ArrFloatingPanel.scss';
import ArrButton from '../arr-button/ArrButton';
import {t} from 'i18next';

export interface ArrFloatingPanelProps {
  buttonTitle: string;
  panelTitle: string;
  children: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  onCancel?: () => void;
}

const ArrFloatingPanel: React.FC<ArrFloatingPanelProps> = ({
  buttonTitle,
  panelTitle,
  children,
  placement = 'bottom',
  onCancel,
}) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const handleMainButtonClick = () => setOpen(!open);
  const handleClose = () => {
    setOpen(false);
    onCancel?.();
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      handleClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <Tooltip
      open={open}
      onClose={handleClose}
      title={
        <Box className="arr-floating-panel" ref={panelRef}>
          <Box className="arr-floating-panel__header">
            <Typography
              className="arr-floating-panel__title  subhead2_size"
              variant="subtitle1"
              data-testid={`arr-floating-panel-typography-panel-title`}
            >
              {panelTitle}
            </Typography>
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={handleClose}
              aria-label={t('common.close')}
              className="arr-floating-panel__close-button"
              dataTestId="arr-floating-panel-close-button"
            >
              {t('common.close')}
            </ArrButton>
          </Box>
          <Box className="arr-floating-panel__content">{children}</Box>
        </Box>
      }
      placement={placement}
      arrow={false}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      slotProps={{
        tooltip: {
          sx: {
            padding: 0,
            minWidth: '250px',
            maxWidth: '350px',
          },
        },
      }}
    >
      <Button
        variant="contained"
        onClick={handleMainButtonClick}
        ref={buttonRef}
        data-testid="arr-floating-panel-button-button-title"
      >
        {buttonTitle}
      </Button>
    </Tooltip>
  );
};

export default ArrFloatingPanel;
